import { api } from '@/boot/axios'
import { useAuthStore } from '@/stores/auth_store'
import urlBase64ToUint8Array from '@/utils/urlBase64ToUint8Array'
import { AxiosError } from 'axios'
import { Loading } from 'quasar'
import { computed, ref } from 'vue'

type DisplayMode =
  | 'browser'
  | 'standalone'
  | 'minimal-ui'
  | 'fullscreen'
  | 'twa'
  | 'window-controls-overlay'
  | 'unknown'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

// === DEBUG MODE ===
// Включить: localStorage.setItem('debug_pwa','1') + перезагрузка
// или DEBUG_PWA=true quasar build -m pwa
const DEBUG = ref(localStorage.getItem('debug_pwa') === '1' || import.meta.env.DEV)
function log(...args: unknown[]) {
  if (DEBUG.value) console.debug('[PWA DEBUG]', ...args)
}
export function setPwaDebug(enabled: boolean) {
  DEBUG.value = enabled
  localStorage.setItem('debug_pwa', enabled ? '1' : '0')
  log('debug', enabled ? 'ON' : 'OFF')
}

// ─── Ожидание завершения auth ──────────────────────────────────────────────
// initPwa() запускается из register-sw.ts (событие SW ready) параллельно
// с boot/auth.ts ($auth.fetch). Чтобы isImpersonating() работало корректно,
// sendDevice ждёт resolveAuthReady() перед отправкой запроса.
let resolveAuthReady!: () => void
const authReadyPromise = new Promise<void>((resolve) => {
  resolveAuthReady = resolve
})

// Вызывается из boot/auth.ts после завершения $auth.fetch()
export function notifyAuthReady() {
  resolveAuthReady()
}

// Возвращает true если текущий сеанс — вход админа под пользователем.
// В этом случае никакие PWA-методы работать не должны.
function isImpersonating(): boolean {
  const $auth = useAuthStore()
  return !!$auth.person?.original_person
}

// reactive state
const appInstalled = ref(false)
const appInstallationAvailable = ref(false)
const notificationAllowed = ref(false)
const notificationAvailable = ref(false)
const pwaProcessing = ref(false)
const isIos = ref(/iphone|ipad|ipod/i.test(navigator.userAgent))
const isIosStandalone = computed(() => (window.navigator as any).standalone === true)

const displayMode = ref<DisplayMode>('unknown')
function detectDisplayMode(): DisplayMode {
  if (document.referrer.startsWith('android-app://')) return 'twa'
  if (isIosStandalone.value) return 'standalone'
  if (window.matchMedia('(display-mode: window-controls-overlay)').matches)
    return 'window-controls-overlay'
  if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen'
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone'
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui'
  return 'browser'
}
const currentMode = computed(() => displayMode.value)

let PWA_InstallPromptEvent: BeforeInstallPromptEvent | undefined
let initialized = false

// ─── Батч-отправка информации об устройстве ────────────────────────────────
// Накапливает поля и отправляет одним запросом после того как auth готов.
type DevicePayload = {
  notifications_allowed?: boolean
  pwa_installed?: boolean
  push_subscription_payload?: PushSubscription | null
}

let pendingDevicePayload: DevicePayload = {}
let deviceFlushScheduled = false

function sendDevice(fields: DevicePayload) {
  Object.assign(pendingDevicePayload, fields)
  if (!deviceFlushScheduled) {
    deviceFlushScheduled = true
    // Ждём authReadyPromise — только после этого isImpersonating() вернёт
    // корректное значение (person уже загружен из $auth.fetch)
    void authReadyPromise.then(() => {
      const payload = pendingDevicePayload
      pendingDevicePayload = {}
      deviceFlushScheduled = false
      if (isImpersonating()) {
        log('sendDevice skipped: impersonating')
        return
      }
      log('sendDevice flush', payload)
      api.post('api/device', payload).catch((e) => {
        if (e instanceof AxiosError && e.response?.status !== 401) {
          console.info(e)
        }
      })
    })
  }
}

// ─── Push ──────────────────────────────────────────────────────────────────

async function pushSubscribe(): Promise<boolean> {
  await authReadyPromise
  if (isImpersonating()) {
    log('pushSubscribe skipped: impersonating')
    return false
  }
  try {
    log('pushSubscribe start')
    const sw = await navigator.serviceWorker.ready
    if (!sw) {
      log('no SW ready')
      return false
    }
    if (Notification.permission !== 'granted') {
      log('permission not granted:', Notification.permission)
      return false
    }

    const subscription = await sw.pushManager.getSubscription()
    log('existing subscription', subscription)

    if (subscription) {
      sendDevice({ push_subscription_payload: subscription })
      return true
    }

    const keyRequest = await api.get('api/device/key')
    const key = typeof keyRequest.data === 'string' ? keyRequest.data : keyRequest.data?.key
    if (!key) throw new Error('VAPID key empty')

    const newSubscription = await sw.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    })

    sendDevice({ push_subscription_payload: newSubscription })
    log('subscribed', newSubscription)

    void notificationRequest()
    return true
  } catch (e) {
    console.error('[PWA] pushSubscribe error', e)
    return false
  }
}

async function pushUnsubscribe() {
  await authReadyPromise
  if (isImpersonating()) {
    log('pushUnsubscribe skipped: impersonating')
    return false
  }
  try {
    const sw = await navigator.serviceWorker.ready
    const subscription = await sw.pushManager.getSubscription()
    if (subscription) {
      await subscription.unsubscribe()
      sendDevice({ push_subscription_payload: null })
    }
    return true
  } catch (e) {
    console.error(e)
    return false
  }
}

// ─── Notifications ─────────────────────────────────────────────────────────

async function notificationRequest() {
  await authReadyPromise
  if (isImpersonating()) {
    log('notificationRequest skipped: impersonating')
    return
  }
  if (!notificationAvailable.value) {
    log('notifications not available')
    return
  }
  if (Notification.permission === 'denied') {
    log('permission denied, cannot request')
    return
  }
  try {
    pwaProcessing.value = true
    const permission = await Notification.requestPermission()
    log('permission result', permission)
    const granted = permission === 'granted'
    notificationAllowed.value = granted

    if (granted) {
      // pushSubscribe внутри уже вызовет sendDevice({ push_subscription_payload })
      // поэтому notifications_allowed отправляем отдельным полем в том же батче

      notificationAllowed.value = await pushSubscribe()
    }

    sendDevice({ notifications_allowed: granted })
  } finally {
    pwaProcessing.value = false
  }
}

// ─── Проверка разрешения на пуши при возвращении на вкладку ───────────────
// Пользователь мог выдать разрешение уже после логина (через настройки браузера
// или при повторном визите), поэтому проверяем каждый раз, когда вкладка
// снова становится видимой.
function handleVisibilityChange() {
  if (document.visibilityState !== 'visible') return
  if (isImpersonating()) return
  if (!notificationAvailable.value) return

  const currentPermission = Notification.permission
  log('visibilitychange, permission:', currentPermission)

  if (currentPermission === 'granted' && !notificationAllowed.value) {
    // Разрешение появилось — подписываемся.
    // pushSubscribe сам вызовет sendDevice({ push_subscription_payload }),
    // notifications_allowed отправляем в том же батче.
    notificationAllowed.value = true
    sendDevice({ notifications_allowed: true })
    void pushSubscribe()
  } else if (currentPermission !== 'granted' && notificationAllowed.value) {
    // Разрешение отозвано
    notificationAllowed.value = false
    sendDevice({ notifications_allowed: false })
  }
}

// ─── Install ───────────────────────────────────────────────────────────────

async function appInstall() {
  if (isImpersonating()) {
    log('appInstall skipped: impersonating')
    return
  }
  if (!PWA_InstallPromptEvent) {
    log('appInstall called but no prompt event')
    return
  }
  try {
    pwaProcessing.value = true
    await PWA_InstallPromptEvent.prompt()
    const choice = await PWA_InstallPromptEvent.userChoice
    log('userChoice', choice)
    if (choice.outcome === 'accepted') {
      Loading.show({ message: 'Идёт установка приложения' })
      // pwaProcessing сбросится в событии 'appinstalled'
    } else {
      pwaProcessing.value = false
    }
  } catch (e) {
    console.error(e)
    pwaProcessing.value = false
  } finally {
    PWA_InstallPromptEvent = undefined
    appInstallationAvailable.value = false
  }
}

// ─── Init ──────────────────────────────────────────────────────────────────

export async function initPwa() {
  if (initialized) return
  initialized = true
  if (typeof window === 'undefined') return

  log('initPwa', { userAgent: navigator.userAgent, isIos: isIos.value })

  // 1. display-mode
  displayMode.value = detectDisplayMode()
  const mqls = ['standalone', 'minimal-ui', 'fullscreen', 'browser', 'window-controls-overlay'].map(
    (m) => window.matchMedia(`(display-mode: ${m})`),
  )
  mqls.forEach((mql) =>
    mql.addEventListener('change', () => {
      displayMode.value = detectDisplayMode()
      log('displayMode changed', displayMode.value)
    }),
  )

  // 2. проверка установки
  try {
    const related = await (navigator as any).getInstalledRelatedApps?.()
    if (related?.length) appInstalled.value = true
  } catch (e) {
    log('getInstalledRelatedApps error', e)
  }

  if (
    displayMode.value === 'standalone' ||
    displayMode.value === 'window-controls-overlay' ||
    isIosStandalone.value
  ) {
    appInstalled.value = true
  }
  log('appInstalled initial', appInstalled.value, 'mode', displayMode.value)

  // 3. события установки - только для Android/Desktop Chromium
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault()
    PWA_InstallPromptEvent = e as BeforeInstallPromptEvent
    appInstallationAvailable.value = true
    appInstalled.value = false
    log('beforeinstallprompt fired')
  })

  window.addEventListener('appinstalled', () => {
    log('appinstalled fired')
    Loading.hide()
    pwaProcessing.value = false
    appInstalled.value = true
    appInstallationAvailable.value = false

    sendDevice({ pwa_installed: true })

    if (
      notificationAvailable.value &&
      Notification.permission !== 'granted' &&
      Notification.permission !== 'denied'
    ) {
      void notificationRequest()
    }
  })

  // 4. notifications
  if ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window) {
    notificationAvailable.value = true
    notificationAllowed.value = Notification.permission === 'granted'
    log('notifications available', notificationAllowed.value)
  } else {
    notificationAvailable.value = false
    log('notifications NOT available')
  }

  // 5. Батч-отправка начального состояния устройства.
  // Все вызовы sendDevice в этом блоке накапливаются и уйдут одним запросом
  // после того как authReadyPromise resolve-ится (см. boot/auth.ts).
  sendDevice({ notifications_allowed: notificationAllowed.value })

  if (appInstalled.value) {
    sendDevice({ pwa_installed: true })
    // pushSubscribe тоже вызовет sendDevice — все поля сольются в один запрос
    if (notificationAllowed.value) void pushSubscribe()
  }

  // 6. Подписка на пуши при возвращении на вкладку —
  // на случай если разрешение было выдано после логина
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

export function usePwa() {
  return {
    appInstalled,
    appInstallationAvailable,
    notificationAllowed,
    notificationAvailable,
    pwaProcessing,
    currentMode,
    isIos,
    isIosStandalone,
    debugEnabled: DEBUG,
    setPwaDebug,
    appInstall,
    notificationRequest,
    pushSubscribe,
    pushUnsubscribe,
  }
}
